import * as Sentry from '@sentry/vue';
import service from '@/services/account';
import integrationService from '@/services/integration';
import { CONSTS, Routes } from '@/helpers/consts';
import { commitSetter } from '@/utils/vuex';
import router from '../router';

import i18n, { changeLocale } from '@/i18n';
import { toastDebounced } from '@/helpers/toasts';

export default {
  loadBaseConfig({ state, dispatch }) {
    return Promise.all([
      dispatch('settings/loadSettings', {}, { root: true }).then(() => {
        changeLocale(state.settings.general.dashboardLanguage);
      }),
    ]);
  },

  loadAccountInfo({ commit, state }) {
    return service
      .getAccountInfo()
      .then(async res => {
        res.data.plansAvailable = res.data.plansAvailable.map(plan => {
          const splitMonth = plan.amountMonth.toString().split('.');
          const splitYear = plan.amountYear.toString().split('.');
          return {
            additionalHeader: plan.topRibbon
              ? i18n.t(`PlanPicker.plan.${plan.topRibbon}`, [20])
              : '',
            icon: plan.icon,
            name: i18n.te(`PlanPicker.plan.name.${plan.nameShort}`)
              ? i18n.t(`PlanPicker.plan.name.${plan.nameShort}.name`)
              : plan.name,
            extraDescription: i18n.te(`PlanPicker.plan.name.${plan.nameShort}`)
              ? i18n.t(`PlanPicker.plan.name.${plan.nameShort}.extraDescription`)
              : 'Custom Plan',
            description: {
              monthlyCredits: plan.monthlyCredits,
              monthlySmsCredits: plan.monthlySmsCredits,
              importQuota: plan.importQuota,
            },
            id: plan.id,
            currentPlanAnnual: plan.currentPlanAnnual,
            annualId: plan.annualId,
            activeFeatures: plan.featuresActive,
            amountMonth: [...splitMonth],
            amountYear: [...splitYear],
            activeNow:
              res.data.planType === 'month'
                ? res.data.activePlanId === plan.id
                : res.data.activePlanId === plan.annualId,
            freeTrial: i18n.t('PlanPicker.plan.freeTrial', [`${plan.freeTrial}`]),
            isCustom: plan.isCustom,
            discountMonth: plan.discountMonth,
            discountYear: plan.discountYear,
            hasDiscountFromOffer: plan.hasDiscountFromOffer,
            hasAnnualDiscountFromOffer: plan.hasAnnualDiscountFromOffer,
          };
        });
        const activePlan = res.data.plansAvailable.find(plan => plan.id === res.data.activePlanId);
        res.data.activePlanAnnualId = (activePlan && activePlan.annualId) || null;

        if (res.data.onboardingSteps.finishSetup === undefined) {
          res.data.onboardingSteps.finishSetup =
            state.accountInfo?.onboardingSteps?.finishSetup ?? false;
        }

        if (process.env.VUE_APP_SENTRY_ENABLED === '1') {
          Sentry.setUser({
            id: res.data.shopId,
            username: res.data.shopDomain,
            email: res.data.shopOwnerEmail,
            ip_address: '{{auto}}',
            planName: res.data.planName,
            planPrice: res.data.planPrice,
            planType: res.data.planType,
          });
        }
        if (res.data.shopNeedsToChoosePlan) {
          const { trialEnded, account } = Routes;
          const onWelcome =
            router.currentRoute.name === 'Welcome' ||
            Boolean(router.currentRoute.query.welcome_screen);
          if (!onWelcome && ![trialEnded, account].includes(router.currentRoute.path)) {
            router.push({
              name: 'TrialEnded',
              hash: router.currentRoute.hash,
            });
          }
        }
        await commit('getAccountInfoSuccess', res.data);
        await commit('setAccountInfoLoading', false);
        return res.data;
      })
      .catch(err => {
        commit('getAccountInfoFailed', err);
        commit('setAccountInfoLoading', false);
        return null;
      });
  },
  loadMetafields({ commit }) {
    return service
      .getMetafieldsJsons()
      .then(res => {
        commit('getMetafieldsJsonsSuccess', res.data);
      })
      .catch(err => {
        commit('getAccountInfoFailed', err);
      });
  },
  loadMetafieldsExample({ commit }) {
    service
      .getMetafieldsJsonsExample()
      .then(res => {
        commit('getMetafieldsJsonsSuccess', res.data);
      })
      .catch(err => {
        commit('getAccountInfoFailed', err);
      });
  },
  showToast(_, toast) {
    toastDebounced(toast);
  },
  setPlanType({ commit }, payload) {
    commit('setPlanType', payload);
  },
  setSDK({ commit }, payload) {
    commit('setSDK', payload);
  },

  setGetStartedProperties({ commit }, payload) {
    commit('setGetStartedProperties', payload);
  },
  updateIntegrations({ commit }, payload) {
    commit('updateAccountInfoIntegrations', payload);
  },
  updateGoogleShoppingCardActive({ commit }, payload) {
    return integrationService.googleSetActive(payload).then(res => {
      commit('updateGoogleShoppingIntegrationActive', res.data.data);
    });
  },
  updateGoogleShoppingCardExcludeImportedReviews({ commit }, payload) {
    return integrationService.googleSetExcludeImportedReviews(payload).then(res => {
      commit('updateGoogleShoppingIntegrationExcludeImportedReviews', res.data.data);
    });
  },
  setKlaviyoStarsColor({ commit }, payload) {
    commit('setKlaviyoStarsColor', payload);
  },
  updateKlaviyoPreferences({ commit }, payload) {
    return integrationService
      .updateKlaviyoPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.klaviyo.updated',
        });
        commit('updateAccountInfoKlaviyoPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.klaviyo.failedToUpdate',
        });
      });
  },
  updateShopifyFlowPreferences({ commit }, payload) {
    return integrationService
      .updateShopifyFlowPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.shopifyFlow.updated',
        });
        commit('updateAccountInfoShopifyFlowPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.shopifyFlow.failedToUpdate',
        });
      });
  },
  syncKlaviyoProfiles({ commit }) {
    return integrationService
      .syncKlaviyoProfiles()
      .then(() => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.klaviyo.syncMessageSuccess',
        });
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.klaviyo.syncMessageError',
        });
      });
  },
  testKlaviyo({ commit }, payload) {
    return integrationService
      .testKlaviyo(payload)
      .then(() => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.testStarted',
        });
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.testError',
        });
      });
  },
  updateLimespotPreferences({ commit }, payload) {
    return integrationService
      .updateLimespotPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.limespot.updated',
        });
        commit('updateAccountInfoLimespotPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.limespot.failedToUpdate',
        });
      });
  },
  updateWiserPreferences({ commit }, payload) {
    return integrationService
      .updateWiserPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.wiser.updated',
        });
        commit('updateAccountInfoWiserPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.wiser.failedToUpdate',
        });
      });
  },
  updateAftershipPreferences({ commit }, payload) {
    return integrationService
      .updateAfterShipPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.afterShip.updated',
        });
        commit('updateAccountInfoAftershipPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.afterShip.failedToUpdate',
        });
      });
  },
  updateGorgiasPreferences({ commit }, payload) {
    return integrationService
      .updateGorgiasPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.gorgias.updated',
        });
        commit('updateAccountInfoGorgiasPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.gorgias.failedToUpdate',
        });
      });
  },
  updateZendeskPreferences({ commit }, payload) {
    return integrationService
      .updateZendeskPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.zendesk.updated',
        });
        commit('updateAccountInfoZendeskPreferences', res.data.data);
        const authUrl = res?.data?.authUrl;
        if (authUrl) {
          window.location.assign(authUrl);
        }
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.zendesk.failedToUpdate',
        });
      });
  },
  updateOmnisendPreferences({ commit }, payload) {
    return integrationService
      .updateOmnisendPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.omnisend.updated',
        });
        commit('updateAccountInfoOmnisendPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.omnisend.failedToUpdate',
        });
      });
  },
  updateAttentivePreferences({ commit }, payload) {
    return integrationService
      .updateAttentivePreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.attentive.updated',
        });
        commit('updateAccountInfoAttentivePreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.attentive.failedToUpdate',
        });
      });
  },
  updateSalesforcePreferences({ commit }, payload) {
    return integrationService
      .updateSalesforcePreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.salesforce.updated',
        });
        commit('updateAccountInfoSalesforcePreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.salesforce.failedToUpdate',
        });
      });
  },
  updateLoyaltylionPreferences({ commit }, payload) {
    return integrationService
      .updateLoyaltylionPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.loyaltylion.updated',
        });
        commit('updateAccountInfoLoyaltyLionPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.loyaltylion.failedToUpdate',
        });
      });
  },
  updateSmileIoPreferences({ commit }, payload) {
    return integrationService
      .updateSmileIoPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.smileIo.updated',
        });
        commit('updateAccountInfoSmileIoPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.smileIo.failedToUpdate',
        });
      });
  },
  updateRiseAiPreferences({ commit }, payload) {
    return integrationService
      .updateRiseAiPreferences(payload)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Integration.riseAi.updated',
        });
        commit('updateAccountInfoRiseAiPreferences', res.data.data);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Integration.riseAi.failedToUpdate',
        });
      });
  },
  loadInstallationInfo: ({ state, commit, dispatch }) => {
    return service
      .getInstallationInfo()
      .then(response => {
        commit('getInstallationInfoSuccess', response.data);
        if (state.installationInfo.installing === true) {
          if (state.globalVars.installationDataInterval == null) {
            state.globalVars.installationDataInterval = setInterval(function () {
              dispatch('loadInstallationInfo');
            }, 5000);
          }
        } else {
          if (state.globalVars.installationDataInterval != null) {
            clearInterval(state.globalVars.installationDataInterval);
            state.globalVars.installationDataInterval = null;
          }
        }
      })
      .catch(err => {
        commit('getInstallationInfoFailed', err);
      });
  },
  updateStepWizard: ({ commit }, payload) => {
    service
      .postStepWizard(payload)
      .then(response => commit('updateStepWizardSuccess', response.data.data));
  },
  updateOnboardingStep: ({ commit }, payload) => {
    service
      .postStepWizard(payload)
      .then(response => commit('updateOnboardingStepSuccess', response.data.data));
  },
  onboardingCompleted: ({ commit }) => {
    commit('showToast', {
      color: 'toast_default',
      text: 'NewOnboarding.toastSuccess',
    });
  },
  getSMSSettings: ({ commit }) => {
    return service.getSMSSettings().then(response => {
      commit('getSMSSettings', response.data.settings);
    });
  },
  getSMSPlansPriceList: ({ commit }) => {
    return service.getSMSPlansPriceList().then(response => {
      commit('getSMSPlansPriceList', response.data.data);
    });
  },
  postSetSmsPriceLimit: ({ commit }, payload) => {
    return service.postSetSmsPriceLimit(payload).then(res => {
      commit('setSmsConfirmLink', res.data.confirmationUrl);
    });
  },
  postDeactivateSmsReviewRequest: () => {
    return service.postDeactivateSmsReviewRequest();
  },
  postActivateSmsReviewRequest: () => {
    return service.postActivateSmsReviewRequest();
  },
  setImporterProfiles({ commit, state }, { profile, reviewSource }) {
    const serviceKey = Object.keys(CONSTS.service).find(
      key => CONSTS.service[key] === reviewSource
    );
    const importerProfiles = state.accountInfo.importerProfiles;

    importerProfiles[serviceKey].reviewStarSubset = profile.reviewStarSubset;
    importerProfiles[serviceKey].translateReviews = profile.translateReviews;
    importerProfiles[serviceKey].howManyReviews = profile.howManyReviews;
    importerProfiles[serviceKey].importOnlyWithImages = profile.importOnlyWithImages;
    importerProfiles[serviceKey].obfuscateReviewerName = profile.obfuscateReviewerName;
    importerProfiles[serviceKey].minimumWords =
      profile.minimumWords === '' ? null : profile.minimumWords;
    if (reviewSource === CONSTS.service.amazon) {
      importerProfiles[serviceKey].importRatings = profile.importRatings;
    }
    service.setImporterProfiles(importerProfiles).then(res => {
      commit('showToast', {
        color: 'toast_default',
        text: 'Email.successSaved',
      });
      return commit('setImporterProfilesSuccess', res);
    });
  },
  showSaverbar({ commit }) {
    commit('setSaverbarActive', true);
    commit('setToolbarActive', false);
  },
  showToastMessage({ commit, dispatch, state }, payload) {
    commit('showToast', payload);
    setTimeout(() => dispatch('hideToastMessage'), state.toastTimeout);
  },
  hideToastMessage({ commit }) {
    commit('hideToast');
  },
  hideSaverbar({ commit }) {
    commit('setSaverbarActive', false);
    commit('setToolbarActive', true);
  },
  decreaseUnansweredCount({ commit }) {
    commit('decreaseUnansweredCount');
  },
  increaseUnpublishedCount({ commit }) {
    commit('increaseUnpublishedCount');
  },
  decreaseUnpublishedCount({ commit }) {
    commit('decreaseUnpublishedCount');
  },
  setSendReviewRequestSms: commitSetter('setSendReviewRequestSms'),
  setSmsPriceLimit: commitSetter('setSmsPriceLimit'),
  setSaveChanges: commitSetter('setSaveChanges'),
  setCancelChanges: commitSetter('setCancelChanges'),
  setToolbarTitle: commitSetter('setToolbarTitle'),
  hideExtensionAnalytics({ commit }) {
    service.hideExtensionAnalytics().then(() => {
      commit('hideExtensionAnalyticsSuccess');
    });
  },
  hideTalkToExperts({ commit }) {
    service.hideTalkToExperts().then(() => {
      commit('hideTalkToExpertsSuccess');
    });
  },
  hideRequestAppStoreReviewWidget({ commit }) {
    service.hideRequestAppStoreReviewWidget().then(() => {
      commit('hideRequestAppStoreReviewWidgetSuccess');
    });
  },
  hideRequestAppStoreReviewModal() {
    return service.hideRequestAppStoreReviewModal();
  },
  postToggleAddon(_, addonName) {
    return service.toggleAddon(addonName).then(res => {
      return res.data.data;
    });
  },
  loadSmartReviewsData({ commit }) {
    return service.loadSmartReviewsData().then(res => {
      commit('loadSmartReviewsDataSuccess', res);
      return res;
    });
  },
  activateSmartReviewsOffer({ dispatch, commit }, enabled) {
    return service
      .activateSmartReviewsOffer(enabled)
      .then(() => {
        commit('showToast', {
          color: 'toast_default',
          text: 'SmartReviews.offerActivated',
        });
        dispatch('loadAccountInfo');
        dispatch('loadSmartReviewsData');
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Account.offerError',
        });
      });
  },
  setSmartReviewsTesting({ commit }, enabled) {
    return service
      .setSmartReviewsTesting(enabled)
      .then(res => {
        commit('showToast', {
          color: 'toast_default',
          text: 'SmartReviews.setTestSuccess',
        });
        return commit('setSmartReviewsTestingSuccess', res);
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Account.snackbarErrorText',
        });
      });
  },
  sendInvitationEmail({ commit }, payload) {
    return service
      .sendReferralInvitationEmail(payload)
      .then(() => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Referral.invitationsSent',
        });
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Account.snackbarErrorText',
        });
      });
  },
  sendInstructionsEmail({ commit }, payload) {
    return service
      .sendInstallationInstructionsEmail(payload)
      .then(() => {
        commit('showToast', {
          color: 'toast_default',
          text: 'Referral.instructionsSent',
        });
      })
      .catch(() => {
        commit('showToast', {
          color: 'toast_error',
          text: 'Account.snackbarErrorText',
        });
      });
  },
  setReferralLeaderboard({ commit }, payload) {
    return service.setReferralLeaderboard(payload).then(res => {
      return commit('setReferralLeaderboard', res);
    });
  },
  updateNoReviewsGuideCount({ commit }) {
    return service.updateNoReviewsGuideCount().then(res => {
      commit('setNoReviewsGuideCount', res);
    });
  },
  setReferralEmailContent: commitSetter('setReferralEmailContent'),

  increaseFeaturedReviewCounter: ({ commit }) => {
    commit('increaseFeaturedReviewCounter');
  },
  updateGetStartedSteps: ({ commit }, payload) => {
    return service.updateGetStartedSteps(payload).then(response => {
      commit('updateGetStartedSteps', response.data.data);
    });
  },
  trackAnalytics: ({ state, getters }, payload) => {
    window.analytics.identify(state.accountInfo.shopId, {
      name: state.accountInfo.shopName,
      email: state.accountInfo.shopOwnerEmail,
      shopId: state.accountInfo.shopId,
    });
    setTimeout(() => {
      window.analytics.track(payload, {
        ...getters.segmentObject,
      });
    }, 10);
  },
  async verifyChromeExtension({ state, commit }) {
    if (state.accountInfo.shopDomain && !state.extension.active) {
      // check extension version by messaging the extension directly
      try {
        const data = {
          event: 'verify',
          shopDomain: state.accountInfo.shopDomain,
          accessToken: state.accountInfo.extensionAuthToken,
          subscription: state.accountInfo.extensionMassImport,
        };
        await chrome.runtime.sendMessage(CONSTS.extensionId, data, response => {
          if (chrome.runtime.lastError) return;
          if (response && response.status === 'success') {
            commit('setExtension', response);
          }
        });
      } catch {
        // no extension found
      }
    }
  },
};
